import { ComponentType, Suspense, lazy, LazyExoticComponent, FC } from 'react';

import CssBaseline from '@material-ui/core/CssBaseline';
import { RouteComponentProps, Router } from '@reach/router';

import Layout from 'components/layout';
import Loader from 'components/loader';
import ReduceProviders from 'components/reduce-providers';
import brandConfigs from 'config/brand-resolution';
import { useAuthContext } from 'state/auth';

import providers from './providers';
import { routes } from './utils/routing';

import './index.css';

type LazyRoute = LazyExoticComponent<ComponentType<any>>;

const CtgLoyalty: LazyRoute = lazy(() => import('./containers/ctg-loyalty'));
const CTGLoyaltyTransaction: LazyRoute = lazy(() => import('./containers/ctg-loyalty-transaction'));
const Customer: LazyRoute = lazy(() => import('./containers/customer'));
const GiftCard: LazyRoute = lazy(() => import('./containers/giftcard'));
const Home: LazyRoute = lazy(() => import('./containers/home'));
const OffersLD: LazyRoute = lazy(() => import('./containers/offers-ld'));
const OffersV1: LazyRoute = lazy(() => import('./containers/offers-unified'));
const OffersV2: LazyRoute = lazy(() => import('./containers/offers-unified'));
const Order: LazyRoute = lazy(() => import('./containers/order'));
const Orders: LazyRoute = lazy(() => import('./containers/orders'));
const ReleaseNotes: LazyRoute = lazy(() => import('./containers/release-notes'));
const SignIn: LazyRoute = lazy(() => import('./containers/sign-in'));
const StoreDetails: LazyRoute = lazy(() => import('./containers/store'));
const SupportHistory: LazyRoute = lazy(() => import('./containers/support-history'));
const Quest: LazyRoute = lazy(() => import('./containers/quest'));
const Quests: LazyRoute = lazy(() => import('./containers/quests'));

const NotFound: FC<RouteComponentProps> = () => <p>Sorry, nothing here</p>;

const ContentRouting = () => {
  const { ctgLoyaltyEnabled } = brandConfigs;
  const { isAuthenticated } = useAuthContext();

  return (
    <Suspense fallback={<Loader />}>
      {isAuthenticated() ? (
        <Router primary={false}>
          <Home path={routes.default} />

          <Customer path={routes.customer} />
          <Customer path={`${routes.customer}/:customerId`} />

          <Orders path={routes.orders} />
          <Orders path={`${routes.customer}/:customerId${routes.orders}`} />
          <Order path={`${routes.orders}/:orderId`} />
          <Order path={`${routes.customer}/:customerId${routes.orders}/:orderId`} />

          <GiftCard path={routes.giftCard} />
          <GiftCard path={`${routes.giftCard}/:isoCountryCode/:cardNumber`} />
          <GiftCard path={`${routes.customer}/:customerId${routes.giftCard}`} />

          {ctgLoyaltyEnabled && (
            <>
              <OffersLD path={routes.offersLD} key={routes.offersLD} />
              <OffersLD path={`${routes.customer}/:customerId${routes.offersLD}`} />
              <OffersV1 path={routes.offersV1} key={routes.offersV1} />
              <OffersV1 path={`${routes.customer}/:customerId${routes.offersV1}`} />
              <OffersV2 path={routes.offersV2} key={routes.offersV2} />
              <OffersV2 path={`${routes.customer}/:customerId${routes.offersV2}`} />
              <CtgLoyalty path={routes.ctgLoyalty} />
              <CtgLoyalty path={`${routes.customer}/:customerId${routes.ctgLoyalty}`} />
              <CTGLoyaltyTransaction
                path={`${routes.customer}/:customerId${routes.ctgLoyaltyTransaction}/:transactionId`}
              />
              <Quests path={routes.quests} />
              <Quests path={`${routes.customer}/:customerId${routes.quests}`} />
              <Quest path={`${routes.customer}/:customerId${routes.quests}/:questId`} />
            </>
          )}

          <StoreDetails path={routes.store} />
          <StoreDetails path={`${routes.store}/:storeId`} />

          <ReleaseNotes path={routes.releaseNotes} />
          <ReleaseNotes path={`${routes.releaseNotes}/:date`} />

          <SupportHistory path={routes.supportHistory} />
          <SupportHistory path={`${routes.customer}/:customerId${routes.supportHistory}`} />
          <NotFound default />
        </Router>
      ) : (
        <SignIn />
      )}
    </Suspense>
  );
};

const App = () => {
  return (
    <ReduceProviders providers={providers}>
      <CssBaseline />
      <Layout>
        <ContentRouting />
      </Layout>
    </ReduceProviders>
  );
};

export default App;
